import * as THREE from "three";
import { MAX_ATTACHED_INSTANCES } from "../core/config";
import { SeededRandom, hashNumbers } from "../core/random";

interface Attachment {
  readonly direction: THREE.Vector3;
  readonly sizeMeters: number;
  readonly twist: number;
  readonly color: THREE.Color;
}

const UP = new THREE.Vector3(0, 1, 0);

export class AttachmentCloud {
  readonly mesh: THREE.InstancedMesh;

  private readonly geometry = new THREE.DodecahedronGeometry(1, 0);
  private readonly material = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    emissive: 0x33445a,
    emissiveIntensity: 0.16,
    roughness: 0.52,
    metalness: 0.06,
    flatShading: true,
    vertexColors: true,
  });
  private readonly dummy = new THREE.Object3D();
  private readonly attachments: Attachment[] = [];
  private nextReplacement = 0;

  constructor() {
    this.mesh = new THREE.InstancedMesh(
      this.geometry,
      this.material,
      MAX_ATTACHED_INSTANCES,
    );
    this.mesh.name = "absorbed-trophy-shell";
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
  }

  get count(): number {
    return this.attachments.length;
  }

  add(id: number, sizeMeters: number, colorHex: number): void {
    const random = new SeededRandom(hashNumbers(id, 0x51_0c_a1));
    const y = random.range(-0.85, 0.92);
    const angle = random.range(0, Math.PI * 2);
    const radial = Math.sqrt(Math.max(0, 1 - y * y));
    const attachment: Attachment = {
      direction: new THREE.Vector3(Math.cos(angle) * radial, y, Math.sin(angle) * radial),
      sizeMeters,
      twist: random.range(0, Math.PI * 2),
      color: new THREE.Color(colorHex).offsetHSL(random.range(-0.07, 0.07), 0, random.range(-0.12, 0.12)),
    };

    if (this.attachments.length < MAX_ATTACHED_INSTANCES) {
      this.attachments.push(attachment);
    } else {
      this.attachments[this.nextReplacement] = attachment;
      this.nextReplacement = (this.nextReplacement + 1) % MAX_ATTACHED_INSTANCES;
    }
    this.mesh.count = this.attachments.length;
  }

  update(ballRadiusLocal: number, renderMetersPerUnit: number): void {
    for (let index = 0; index < this.attachments.length; index += 1) {
      const attachment = this.attachments[index];
      if (!attachment) {
        continue;
      }
      const visibleSize = THREE.MathUtils.clamp(
        attachment.sizeMeters / renderMetersPerUnit,
        ballRadiusLocal * 0.035,
        ballRadiusLocal * 0.2,
      );
      this.dummy.position.copy(attachment.direction).multiplyScalar(ballRadiusLocal + visibleSize * 0.3);
      this.dummy.quaternion.setFromUnitVectors(UP, attachment.direction);
      this.dummy.rotateY(attachment.twist);
      this.dummy.scale.set(visibleSize, visibleSize * 0.72, visibleSize);
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(index, this.dummy.matrix);
      this.mesh.setColorAt(index, attachment.color);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) {
      this.mesh.instanceColor.needsUpdate = true;
    }
  }

  clear(): void {
    this.attachments.length = 0;
    this.nextReplacement = 0;
    this.mesh.count = 0;
  }

  dispose(): void {
    this.mesh.dispose();
    this.geometry.dispose();
    this.material.dispose();
  }
}
